import { styled } from '@mui/material'
import { IconContext } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { ComponentProps, ForwardedRef, ReactNode } from 'react'

export type ContentChipColor = 'royal' | 'blue' | 'turquoise' | 'green' | 'leaf' | 'lime' | 'orange' | 'red' | 'grape' | 'purple' | 'grey'

export type ContentChipProps = {
    size: 'small' | 'medium'
    highContrast?: boolean
    color: ContentChipColor
    label: ReactNode
    icon?: ReactNode
    ref?: ForwardedRef<HTMLDivElement>
}

export function ContentChip({ size, color, label, icon, highContrast, ref }: ContentChipProps) {
    return (
        <StyledContentChip $color={color} $highContrast={!!highContrast} ref={ref}>
            {icon && (
                <IconContext.Provider
                    value={{
                        color: highContrast ? CONTENT_CHIP_CONTRAST_COLORS[color].icon : CONTENT_CHIP_COLORS[color].icon,
                        size: tokens.SizingIconS,
                        weight: 'bold',
                    }}
                >
                    {icon}
                </IconContext.Provider>
            )}
            <Typography variant={CONTENT_CHIP_SIZES[size]} noWrap>
                {label}
            </Typography>
        </StyledContentChip>
    )
}

const StyledContentChip = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: ContentChipProps['color']; $highContrast: boolean }>(({ $color, $highContrast }) => {
    const colors = $highContrast ? CONTENT_CHIP_CONTRAST_COLORS[$color] : CONTENT_CHIP_COLORS[$color]

    return {
        display: 'inline-flex',
        flexDirection: 'row',
        gap: tokens.SizingSpacing075,
        alignItems: 'center',
        width: 'fit-content',
        minWidth: '22px',
        justifyContent: 'center',
        height: 'fit-content',
        borderRadius: tokens.SizingCornerS,
        padding: `${tokens.SizingSpacing025} ${tokens.SizingSpacing075}`,
        backgroundColor: colors.backgroundColor,
        color: colors.color,
    }
})

const CONTENT_CHIP_SIZES: Record<ContentChipProps['size'], ComponentProps<typeof Typography>['variant']> = {
    small: 'textS',
    medium: 'textM',
}

export const CONTENT_CHIP_COLORS: Record<ContentChipProps['color'], { backgroundColor: string; color: string; icon: string }> = {
    royal: {
        backgroundColor: tokens.ColorSurfaceAccentRoyalDefault,
        color: tokens.ColorTextAccentRoyal,
        icon: tokens.ColorIconAccentRoyal,
    },
    blue: {
        backgroundColor: tokens.ColorSurfaceAccentBlueDefault,
        color: tokens.ColorTextAccentBlue,
        icon: tokens.ColorIconAccentBlue,
    },
    turquoise: {
        backgroundColor: tokens.ColorSurfaceAccentTurquoiseDefault,
        color: tokens.ColorTextAccentTurquoise,
        icon: tokens.ColorIconAccentTurquoise,
    },
    green: {
        backgroundColor: tokens.ColorSurfaceAccentGreenDefault,
        color: tokens.ColorTextAccentGreen,
        icon: tokens.ColorIconAccentGreen,
    },
    leaf: {
        backgroundColor: tokens.ColorSurfaceAccentLeafDefault,
        color: tokens.ColorTextAccentLeaf,
        icon: tokens.ColorIconAccentLeaf,
    },
    lime: {
        backgroundColor: tokens.ColorSurfaceAccentLimeDefault,
        color: tokens.ColorTextAccentLime,
        icon: tokens.ColorIconAccentLime,
    },
    orange: {
        backgroundColor: tokens.ColorSurfaceAccentOrangeDefault,
        color: tokens.ColorTextAccentOrange,
        icon: tokens.ColorIconAccentOrange,
    },
    red: {
        backgroundColor: tokens.ColorSurfaceAccentRedDefault,
        color: tokens.ColorTextAccentRed,
        icon: tokens.ColorIconAccentRed,
    },
    grape: {
        backgroundColor: tokens.ColorSurfaceAccentGrapeDefault,
        color: tokens.ColorTextAccentGrape,
        icon: tokens.ColorIconAccentGrape,
    },
    purple: {
        backgroundColor: tokens.ColorSurfaceAccentPurpleDefault,
        color: tokens.ColorTextAccentPurple,
        icon: tokens.ColorIconAccentPurple,
    },
    grey: {
        backgroundColor: tokens.ColorSurfaceAccentGreyDefault,
        color: tokens.ColorTextAccentGrey,
        icon: tokens.ColorIconAccentGrey,
    },
}

export const CONTENT_CHIP_CONTRAST_COLORS: Record<ContentChipProps['color'], { backgroundColor: string; color: string; icon: string }> = {
    royal: {
        backgroundColor: tokens.ColorSurfaceAccentRoyalBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    blue: {
        backgroundColor: tokens.ColorSurfaceAccentBlueBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    turquoise: {
        backgroundColor: tokens.ColorSurfaceAccentTurquoiseBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    green: {
        backgroundColor: tokens.ColorSurfaceAccentGreenBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    leaf: {
        backgroundColor: tokens.ColorSurfaceAccentLeafBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    lime: {
        backgroundColor: tokens.ColorSurfaceAccentLimeBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    orange: {
        backgroundColor: tokens.ColorSurfaceAccentOrangeBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    red: {
        backgroundColor: tokens.ColorSurfaceAccentRedBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    grape: {
        backgroundColor: tokens.ColorSurfaceAccentGrapeBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    purple: {
        backgroundColor: tokens.ColorSurfaceAccentPurpleBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
    grey: {
        backgroundColor: tokens.ColorSurfaceAccentGreyBold,
        color: tokens.ColorTextInverse,
        icon: tokens.ColorIconInverse,
    },
}
